import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { loginSuccess } from '../store/slices/authslice';
import Login from '../pages/Login/Login';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if there's a valid token in localStorage
    const accessToken = localStorage.getItem('accessToken');
    const userStr = localStorage.getItem('user');

    if (accessToken && userStr && !isAuthenticated) {
      try {
        const storedUser = JSON.parse(userStr);
        // Restore user session from localStorage (including role)
        dispatch(loginSuccess({
          id: storedUser.id,
          name: storedUser.name || `${storedUser.firstName || ''} ${storedUser.lastName || ''}`.trim() || 'User',
          mobile: storedUser.phoneNumber || '',
          email: storedUser.email || '',
          role: storedUser.role
        }));
      } catch {
        // Invalid stored data, clear it
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, [dispatch, isAuthenticated]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Check both Redux state and localStorage token
  const hasToken = !!localStorage.getItem('accessToken');

  if (!isAuthenticated && !hasToken) {
    return <Login />;
  }

  // Redirect admin users to /admin when accessing the root path
  const storedUser = localStorage.getItem('user');
  if (storedUser && location.pathname === '/') {
    try {
      const userData = JSON.parse(storedUser);
      if (userData.role === 'admin' || userData.role === 'superadmin') {
        return <Navigate to="/admin" replace />;
      }
      if (userData.role === 'sales') {
        return <Navigate to="/sales" replace />;
      }
    } catch {
      // Ignore parse errors
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;